import { useEffect, useId, useState, type ReactElement } from "react";
import { IconLock, IconLockOpen, IconTrash } from "@tabler/icons-react";
import type { StructureEarthwork } from "../../state/computeStructureEarthwork";
import type { PlacedStructure, StructuresState } from "../../state/structuresState";
import {
  MAX_GRADE_OFFSET_FT,
  MAX_ROTATION_DEG,
  MIN_GRADE_OFFSET_FT,
  MIN_ROTATION_DEG,
  clampRotationDeg,
  structureFootprintSqFt
} from "../../state/structuresState";
import type { InspectorPresentation } from "./BaseLayersCard";
import { InspectorChipButton, InspectorChipButtonRow } from "./InspectorChipButton";
import { InspectorCallout } from "./InspectorCallout";
import { InspectorRow } from "./InspectorRow";
import { Spacer } from "../Spacer";
import { SubsectionHeader } from "./SubsectionHeader";
import styles from "./StructuresCard.module.css";

export interface StructureBoundsFt {
  minXFt: number;
  maxXFt: number;
  minYFt: number;
  maxYFt: number;
}

export interface StructuresCardProps {
  state: StructuresState;
  /** Parcel-ring axis-aligned bbox in feet — used for X/Y slider ranges. */
  bounds: StructureBoundsFt;
  /**
   * When false, the report is shared read-only with this viewer: structures
   * are visible but every edit control is hidden/disabled and a "request
   * access" notice is shown. Defaults to true (owner / editable share).
   */
  editable?: boolean;
  onBeginDraw: () => void;
  /**
   * Trigger one-click auto-placement of a new structure on the flattest
   * qualifying region of the parcel. Owner code (main.ts) runs the placer
   * and commits — the card just dispatches the intent.
   */
  onAutoPlace: () => void;
  /**
   * False when no buildable region is available (e.g. the lot is too steep
   * or the analysis hasn't run yet). When false, the auto-place button is
   * rendered disabled with a tooltip hint.
   */
  canAutoPlace: boolean;
  /**
   * Returns the reasoning sentence the placer wrote for `structureId`, or
   * null if the structure was hand-drawn (or the reasoning was cleared
   * after a user edit). Rendered inside the selected structure's editor.
   */
  getAutoPlacementReasoning: (structureId: string) => string | null;
  onStructureUpdate: (id: string, partial: Partial<PlacedStructure>) => void;
  onStructureDelete: (id: string) => void;
  /**
   * Select (or deselect) a structure for editing. Selection is exclusive —
   * `structure.expanded` is true for at most one structure at a time (see
   * `toggleStructureSectionExclusive` in structuresState).
   */
  onSectionToggle: (id: string) => void;
  /** Cut/fill estimate per structure. Returns null when DEM not yet ready. */
  getEarthworkForStructure: (structure: PlacedStructure) => StructureEarthwork | null;
  presentation?: InspectorPresentation;
}

/**
 * Structures panel: a compact list of placed structures plus ONE editor for
 * the selected one (dimensions, placement, earthwork). The multi-structure
 * cut/fill table lives in the report's Site Work section.
 */
export function StructuresCard({
  state,
  bounds,
  editable = true,
  onBeginDraw,
  onAutoPlace,
  canAutoPlace,
  getAutoPlacementReasoning,
  onStructureUpdate,
  onStructureDelete,
  onSectionToggle,
  getEarthworkForStructure,
  presentation = "inspector"
}: StructuresCardProps): ReactElement {
  const drawerRail = presentation === "drawer";
  const selected = state.list.find((s) => s.expanded) ?? null;

  return (
    <div>
      {!editable ? (
        <p className={styles.readOnlyNotice} role="note">
          This report was shared with you in view-only mode. Request access
          from the report owner to edit structures.
        </p>
      ) : null}

      {state.list.length === 0 ? (
        <p className={styles.emptyState}>
          {editable ? (
            <>
              No structures yet. <em>Auto-place</em> one on the best buildable
              shelf, or <em>Add structure</em> and drag inside the parcel.
            </>
          ) : (
            "No structures have been placed on this report yet."
          )}
        </p>
      ) : (
        <ul className={styles.list} role="listbox" aria-label="Placed structures">
          {state.list.map((s) => (
            <StructureListRow
              key={s.id}
              structure={s}
              selected={s.expanded}
              editable={editable}
              onSelect={() => onSectionToggle(s.id)}
              onToggleLock={() => onStructureUpdate(s.id, { locked: !s.locked })}
              onDelete={() => onStructureDelete(s.id)}
            />
          ))}
        </ul>
      )}

      {selected != null ? (
        <StructureEditor
          key={selected.id}
          structure={selected}
          bounds={bounds}
          editable={editable}
          reasoning={getAutoPlacementReasoning(selected.id)}
          earthwork={getEarthworkForStructure(selected)}
          onUpdate={(partial) => onStructureUpdate(selected.id, partial)}
        />
      ) : null}

      {editable ? (
        <>
          <Spacer size="sm" noBorder />
          <InspectorChipButtonRow>
            <InspectorChipButton
              drawerRail={drawerRail}
              onClick={onAutoPlace}
              disabled={!canAutoPlace}
              title={
                canAutoPlace
                  ? "Place a default-size structure on the next-best buildable region. Click again to fill the runner-up region."
                  : "No buildable region available — every qualifying flat shelf already has a structure on it."
              }
            >
              Auto-place
            </InspectorChipButton>
            <InspectorChipButton
              drawerRail={drawerRail}
              className={
                state.drawModeActive ? styles.addStructureChipActive : undefined
              }
              onClick={onBeginDraw}
              aria-pressed={state.drawModeActive}
            >
              {state.drawModeActive ? "Drawing… click on parcel" : "Add structure"}
            </InspectorChipButton>
          </InspectorChipButtonRow>
        </>
      ) : null}
    </div>
  );
}

/* ─── List row ─────────────────────────────────────────────────────── */

interface StructureListRowProps {
  structure: PlacedStructure;
  selected: boolean;
  editable: boolean;
  onSelect: () => void;
  onToggleLock: () => void;
  onDelete: () => void;
}

function StructureListRow({
  structure,
  selected,
  editable,
  onSelect,
  onToggleLock,
  onDelete
}: StructureListRowProps): ReactElement {
  return (
    <li
      className={`${styles.listRow}${selected ? ` ${styles.listRowSelected}` : ""}`}
      role="option"
      aria-selected={selected}
    >
      <button
        type="button"
        className={styles.listRowMain}
        onClick={onSelect}
        aria-label={`${selected ? "Deselect" : "Edit"} ${structure.name}`}
      >
        <span className={styles.listRowDot} aria-hidden="true" />
        <span className={styles.listRowName}>{structure.name}</span>
        <span className={styles.listRowFootprint}>
          {formatFootprintLabel(structure)}
        </span>
      </button>
      {editable ? (
        <span className={styles.listRowActions}>
          <button
            type="button"
            className={styles.iconBtn}
            aria-label={
              structure.locked
                ? `Unlock ${structure.name}`
                : `Lock ${structure.name}`
            }
            aria-pressed={structure.locked}
            title={structure.locked ? "Unlock" : "Lock"}
            onClick={onToggleLock}
          >
            {structure.locked ? (
              <IconLock size={16} stroke={1.5} />
            ) : (
              <IconLockOpen size={16} stroke={1.5} />
            )}
          </button>
          <button
            type="button"
            className={`${styles.iconBtn} ${styles.iconBtnDanger}`}
            aria-label={`Delete ${structure.name}`}
            title="Delete"
            disabled={structure.locked}
            onClick={onDelete}
          >
            <IconTrash size={16} stroke={1.5} />
          </button>
        </span>
      ) : null}
    </li>
  );
}

/* ─── Editor ───────────────────────────────────────────────────────── */

interface StructureEditorProps {
  structure: PlacedStructure;
  bounds: StructureBoundsFt;
  editable: boolean;
  reasoning: string | null;
  earthwork: StructureEarthwork | null;
  onUpdate: (partial: Partial<PlacedStructure>) => void;
}

function StructureEditor({
  structure,
  bounds,
  editable,
  reasoning,
  earthwork,
  onUpdate
}: StructureEditorProps): ReactElement {
  const reactId = useId();
  const locked = structure.locked || !editable;

  const sliderCommon = {
    sliderLabelWide: false,
    step: 1,
    onSliderCommit: () => {},
    parseBoxValue: (raw: string): number | undefined => {
      const n = Number.parseFloat(raw);
      return Number.isFinite(n) ? n : undefined;
    },
    formatBoxDraft: (v: number): string => String(Math.round(v))
  };

  return (
    <div
      className={`${styles.editor}${locked ? ` ${styles.editorLocked}` : ""}`}
      aria-disabled={locked}
    >
      {reasoning ? <InspectorCallout>{reasoning}</InspectorCallout> : null}

      <SubsectionHeader label="Dimensions (ft)" />
      <div className={styles.dimGrid}>
        <DimensionField
          id={`${reactId}-w`}
          label="W"
          title="Width"
          value={structure.widthFt}
          onCommit={(v) => onUpdate({ widthFt: v })}
        />
        <DimensionField
          id={`${reactId}-d`}
          label="D"
          title="Depth"
          value={structure.depthFt}
          onCommit={(v) => onUpdate({ depthFt: v })}
        />
        <DimensionField
          id={`${reactId}-h`}
          label="H"
          title="Height"
          value={structure.heightFt}
          onCommit={(v) => onUpdate({ heightFt: v })}
        />
      </div>

      <SubsectionHeader label="Placement" />
      <InspectorRow
        label="East / west"
        labelWeight="normal"
        fieldRow
        inspectorSliderTrailing={{
          ...sliderCommon,
          sliderId: `${reactId}-x-r`,
          boxInputId: `${reactId}-x-i`,
          ariaLabel: "Structure X position east/west in feet",
          min: Math.round(bounds.minXFt),
          max: Math.round(bounds.maxXFt),
          value: structure.centerXFt,
          boxDisplayValue: String(Math.round(structure.centerXFt)),
          onSliderInput: (next) => onUpdate({ centerXFt: next }),
          onBoxParsed: (v) => onUpdate({ centerXFt: v })
        }}
      />
      <InspectorRow
        label="North / south"
        labelWeight="normal"
        fieldRow
        inspectorSliderTrailing={{
          ...sliderCommon,
          sliderId: `${reactId}-y-r`,
          boxInputId: `${reactId}-y-i`,
          ariaLabel: "Structure Y position north/south in feet",
          min: Math.round(bounds.minYFt),
          max: Math.round(bounds.maxYFt),
          value: structure.centerYFt,
          boxDisplayValue: String(Math.round(structure.centerYFt)),
          onSliderInput: (next) => onUpdate({ centerYFt: next }),
          onBoxParsed: (v) => onUpdate({ centerYFt: v })
        }}
      />
      <InspectorRow
        label="Rotation (°)"
        labelWeight="normal"
        fieldRow
        inspectorSliderTrailing={{
          ...sliderCommon,
          sliderId: `${reactId}-rot-r`,
          boxInputId: `${reactId}-rot-i`,
          ariaLabel: "Structure rotation in degrees",
          min: MIN_ROTATION_DEG,
          max: MAX_ROTATION_DEG,
          value: structure.rotationDeg,
          boxDisplayValue: String(Math.round(structure.rotationDeg)),
          onSliderInput: (next) => onUpdate({ rotationDeg: clampRotationDeg(next) }),
          parseBoxValue: (raw) => {
            const n = Number.parseFloat(raw);
            return Number.isFinite(n) ? clampRotationDeg(n) : undefined;
          },
          onBoxParsed: (v) => onUpdate({ rotationDeg: v })
        }}
      />
      <InspectorRow
        label="Grade (ft)"
        labelWeight="normal"
        fieldRow
        inspectorSliderTrailing={{
          ...sliderCommon,
          sliderId: `${reactId}-z-r`,
          boxInputId: `${reactId}-z-i`,
          ariaLabel: "Structure grade offset in feet",
          min: MIN_GRADE_OFFSET_FT,
          max: MAX_GRADE_OFFSET_FT,
          value: structure.gradeOffsetFt,
          boxDisplayValue: String(Math.round(structure.gradeOffsetFt)),
          onSliderInput: (next) => onUpdate({ gradeOffsetFt: next }),
          parseBoxValue: (raw) => {
            const n = Number.parseFloat(raw);
            if (!Number.isFinite(n)) return undefined;
            if (n < MIN_GRADE_OFFSET_FT || n > MAX_GRADE_OFFSET_FT) return undefined;
            return n;
          },
          onBoxParsed: (v) => onUpdate({ gradeOffsetFt: v })
        }}
      />

      <SubsectionHeader label="Earthwork (yd³)" />
      <div className={styles.earthwork} title="Cut / fill estimate for this structure's pad. Net: + export, − import.">
        <EarthworkStat label="Cut" value={earthwork ? formatYd3(earthwork.cutYd3) : "—"} />
        <EarthworkStat label="Fill" value={earthwork ? formatYd3(earthwork.fillYd3) : "—"} />
        <EarthworkStat label="Net" value={earthwork ? formatSignedYd3(earthwork.netYd3) : "—"} emphasis />
      </div>
    </div>
  );
}

function EarthworkStat({
  label,
  value,
  emphasis = false
}: {
  label: string;
  value: string;
  emphasis?: boolean;
}): ReactElement {
  return (
    <span className={styles.earthworkStat}>
      <span className={styles.earthworkStatLabel}>{label}</span>
      <span
        className={`${styles.earthworkStatValue}${emphasis ? ` ${styles.earthworkStatValueEmphasis}` : ""}`}
      >
        {value}
      </span>
    </span>
  );
}

function formatFootprintLabel(s: PlacedStructure): string {
  const sq = Math.round(structureFootprintSqFt(s));
  return `${sq.toLocaleString("en-US")} sf`;
}

function formatYd3(value: number): string {
  const rounded = Math.round(value);
  if (rounded === 0) return "0";
  return rounded.toLocaleString("en-US");
}

function formatSignedYd3(value: number): string {
  const rounded = Math.round(value);
  if (rounded === 0) return "0";
  // Use a real Unicode minus so the column stays well aligned.
  if (rounded > 0) return `+${rounded.toLocaleString("en-US")}`;
  return `\u2212${Math.abs(rounded).toLocaleString("en-US")}`;
}

/* ─── Dimension field ──────────────────────────────────────────────── */

interface DimensionFieldProps {
  id: string;
  /** One-letter axis label shown in the cell. */
  label: string;
  /** Full name for tooltips / aria. */
  title: string;
  value: number;
  onCommit: (v: number) => void;
}

const MIN_DIMENSION_FT = 1;

/**
 * Compact numeric cell for W / D / H. Type a value, or nudge with the
 * arrow keys / mouse wheel; the ± affordances are the arrow keys so the
 * three cells fit side by side in the 248px panel body.
 */
function DimensionField({
  id,
  label,
  title,
  value,
  onCommit
}: DimensionFieldProps): ReactElement {
  const [draft, setDraft] = useState(String(Math.round(value)));
  const [focused, setFocused] = useState(false);
  const rounded = Math.round(value);

  useEffect(() => {
    if (!focused) setDraft(String(Math.round(value)));
  }, [value, focused]);

  function commit(raw: string): void {
    const n = Number.parseFloat(raw);
    if (!Number.isFinite(n) || n < MIN_DIMENSION_FT) {
      setDraft(String(rounded));
      return;
    }
    onCommit(Math.round(n));
  }

  function step(delta: number): void {
    const parsed = Number.parseFloat(draft);
    const base = focused && Number.isFinite(parsed) ? Math.round(parsed) : rounded;
    const next = Math.max(MIN_DIMENSION_FT, base + delta);
    onCommit(next);
    setDraft(String(next));
  }

  return (
    <label className={styles.dimField} htmlFor={id} title={`${title} (ft)`}>
      <span className={styles.dimFieldLabel}>{label}</span>
      <input
        id={id}
        className={styles.dimFieldInput}
        type="text"
        inputMode="numeric"
        autoComplete="off"
        aria-label={`${title} in feet`}
        value={draft}
        spellCheck={false}
        onFocus={(e) => {
          setFocused(true);
          e.target.select();
        }}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => {
          commit(draft);
          setFocused(false);
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            commit(draft);
            (e.target as HTMLInputElement).blur();
          } else if (e.key === "ArrowUp") {
            e.preventDefault();
            step(e.shiftKey ? 10 : 1);
          } else if (e.key === "ArrowDown") {
            e.preventDefault();
            step(e.shiftKey ? -10 : -1);
          }
        }}
      />
      <span className={styles.dimFieldSteppers} aria-hidden="true">
        <button
          type="button"
          tabIndex={-1}
          className={styles.dimStep}
          onClick={() => step(1)}
        >
          ▴
        </button>
        <button
          type="button"
          tabIndex={-1}
          className={styles.dimStep}
          disabled={rounded <= MIN_DIMENSION_FT}
          onClick={() => step(-1)}
        >
          ▾
        </button>
      </span>
    </label>
  );
}
